import { useCallback, useState } from "react";

// "auto" is the original responsive behavior (columns reflow by available
// width, capped at 3) — a fixed number pins the live grid to exactly that
// many columns regardless of viewport width.
export type GridColumns = "auto" | 2 | 3 | 4 | 5;

const STORAGE_KEY = "mtn-cctv-live-grid-columns";

function readStored(): GridColumns {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "auto") return stored;
    const n = Number(stored);
    if (n === 2 || n === 3 || n === 4 || n === 5) return n;
  } catch {
    // Private browsing / storage disabled: fall through to the default.
  }
  return "auto";
}

// Persists the viewer's preferred live-grid density across reloads. Purely
// a per-viewer display preference — nothing here is shared with other
// viewers or synced to the backend, same reasoning as useColorMode.
export function useGridColumns(): [GridColumns, (columns: GridColumns) => void] {
  const [columns, setColumns] = useState<GridColumns>(readStored);

  const setAndStore = useCallback((next: GridColumns) => {
    setColumns(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // Best-effort: the in-memory state still updates for this session.
    }
  }, []);

  return [columns, setAndStore];
}
